import { useImperativeHandle } from 'react';
import {
  Divider,
  DropdownList,
  Stack,
  Text,
  ToggleListItem,
} from '@adc/parallax-component-library';
import { atom, useAtom } from 'jotai';

import { DropdownInput } from '~/components/DropdownInput';
import {
  type PatientSettingsFormProps,
  type PatientSettingsFormValues,
} from '~/features/patientCreation/types';
import { buildTestId } from '~/utils/buildTestId';

import {
  HIGH_LACTATE_ALERT_DEFAULT_INDEX,
  HIGH_LACTATE_ALERT_DURATION_DEFAULT_INDEX,
  HIGH_LACTATE_ALERT_DURATION_ITEMS,
  HIGH_LACTATE_ALERT_DURATION_ITEMS_LABELS,
  HIGH_LACTATE_ALERT_ITEMS,
  HIGH_LACTATE_ALERT_ITEMS_LABELS,
} from './constants';

const settingsAtom = atom<PatientSettingsFormValues>({
  displayLactateData: true,
  highLactateAlert: true,
  highLactateAlertAbove: HIGH_LACTATE_ALERT_DEFAULT_INDEX,
  highLactateAlertDuration: HIGH_LACTATE_ALERT_DURATION_DEFAULT_INDEX,
});

export const PatientSettingsForm = ({
  ref,
  testID,
}: PatientSettingsFormProps) => {
  const [settings, setSettings] = useAtom(settingsAtom);

  const alertAboveLabelValue =
    settings.highLactateAlertAbove !== undefined
      ? HIGH_LACTATE_ALERT_ITEMS_LABELS[settings.highLactateAlertAbove]
      : undefined;

  const alertDurationLabelValue =
    settings.highLactateAlertDuration !== undefined
      ? HIGH_LACTATE_ALERT_DURATION_ITEMS_LABELS[
          settings.highLactateAlertDuration
        ]
      : undefined;

  useImperativeHandle(
    ref,
    () => ({
      getValues: () => ({
        ...settings,
        // Sent values only if high lactate alert is enabled
        ...(settings.highLactateAlert
          ? {
              highLactateAlertAbove:
                settings.highLactateAlertAbove !== undefined
                  ? HIGH_LACTATE_ALERT_ITEMS[settings.highLactateAlertAbove]
                      ?.value
                  : undefined,
              highLactateAlertDuration:
                settings.highLactateAlertDuration !== undefined
                  ? HIGH_LACTATE_ALERT_DURATION_ITEMS[
                      settings.highLactateAlertDuration
                    ]?.value
                  : undefined,
            }
          : {
              highLactateAlertAbove: undefined,
              highLactateAlertDuration: undefined,
            }),
      }),
    }),
    [settings],
  );

  return (
    <Stack testID={testID}>
      <Divider backgroundColor="$gray4Light" marginTop="$4" />

      {/* Display Lactate Data */}
      <Stack marginHorizontal={-16}>
        <ToggleListItem
          testID={buildTestId({
            testID,
            suffix: ['toggle', 'display', 'lactate', 'data'],
          })}
          title="Display patient’s lactate data"
          description="When turned “On”, lactate values will be visible to the patient on their mobile app. When “Off,” patients will not see lactate values but will still receive high lactate alerts"
          checked={settings.displayLactateData}
          onPress={(checked) => {
            setSettings((prev) => ({
              ...prev,
              displayLactateData: checked,
            }));
          }}
        />
      </Stack>

      <Divider backgroundColor="$gray4Light" marginBottom="$4" />

      {/* High Lactate Alert Toggle */}
      <Stack marginHorizontal={-16}>
        <ToggleListItem
          testID={buildTestId({
            testID,
            suffix: ['toggle', 'high', 'lactate', 'alert'],
          })}
          title="High Lactate Alert"
          checked={settings.highLactateAlert}
          onPress={(checked) => {
            setSettings((prev) => ({
              ...prev,
              highLactateAlert: checked,
            }));
          }}
        />
      </Stack>

      {/* High Lactate Alert Settings */}
      {settings.highLactateAlert && (
        <Stack gap="$4">
          <Text type="$bodySmall.default" color="$text.60">
            This alert will be triggered when the patient’s lactate value is:
          </Text>

          <Stack
            flexDirection="row"
            justifyContent="space-between"
            gap="$6"
            marginBottom="$5"
          >
            {/* Alert Above */}
            <Stack flex={1}>
              <DropdownList
                testID={buildTestId({
                  testID,
                  suffix: ['dropdown', 'high', 'lactate', 'alert', 'above'],
                })}
                items={HIGH_LACTATE_ALERT_ITEMS_LABELS}
                selectedIndex={settings.highLactateAlertAbove}
                onSelect={(selectedIndex) => {
                  setSettings((prev) => ({
                    ...prev,
                    highLactateAlertAbove: selectedIndex,
                  }));
                }}
              >
                <DropdownInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'high', 'lactate', 'alert', 'above'],
                  })}
                  aria-label='"High Lactate Alert Above"'
                  label="At or above"
                  value={alertAboveLabelValue}
                />
              </DropdownList>
            </Stack>

            {/* Alert Duration */}
            <Stack flex={1}>
              <DropdownList
                testID={buildTestId({
                  testID,
                  suffix: ['dropdown', 'high', 'lactate', 'alert', 'duration'],
                })}
                items={HIGH_LACTATE_ALERT_DURATION_ITEMS_LABELS}
                selectedIndex={settings.highLactateAlertDuration}
                onSelect={(selectedIndex) => {
                  setSettings((prev) => ({
                    ...prev,
                    highLactateAlertDuration: selectedIndex,
                  }));
                }}
              >
                <DropdownInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'high', 'lactate', 'alert', 'duration'],
                  })}
                  aria-label='"High Lactate Alert Duration"'
                  label="For at least"
                  value={alertDurationLabelValue}
                />
              </DropdownList>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
